import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { VERSION, CAMPAIGN_STATUS } from 'utils/campaign';
import { PLAN_CODE, SUBSCRIPTION_STATUS } from 'utils/user';
import env from 'utils/env';
import LoadingLogo from 'shared-components/LoadingLogo/LoadingLogo';
import withDocumentTitle from './HOC/withDocumentTitle/withDocumentTitle';

import ClosedAddOns from './Scenes/ClosedSale/ClosedAddOns';
import ClosedEdit from './Scenes/ClosedSale/ClosedEdit';
import ClosedSocialPage from './Scenes/ClosedSocial/ClosedSocialPage';
import Sale from './Scenes/Sale/Sale';
import ErrorPage from './Scenes/ErrorPages/ErrorPage';

const redirectToSubscribe = () => {
  window.location.replace(`https://${env.hostname}/subscribe-today`);
  return null;
};

const redirectToReactivate = () => {
  window.location.replace(`https://${env.hostname}/reactivate-my-membership`);
  return null;
};

const Scene = ({
  user,
  campaign,
  campaignIsOpen,
  isSocialEcomm,
  version,
  fetchingCart,
  fetchingPreviousCart,
  displaySocialBanner,
}) => {
  const {
    socialEcomm: socialEcommFF,
    freeTierEvergreenShopAccess: freeTierEvergreenShopAccessFF,
  } = useFlags();
  const {
    campaignError,
    fetchingCampaign,
    campaignStatus,
    userSocialAccess,
  } = campaign;
  const {
    fetchingUserStatus,
    planCode,
    isAccountPastDue,
    subscriptionState,
    loggedIn,
  } = user;
  const isRedeemedFriend = planCode === PLAN_CODE.fffsocial;

  if (campaignError) return <ErrorPage />;
  const isCartDataLoading = fetchingCart && fetchingPreviousCart;
  if (
    fetchingCampaign ||
    fetchingUserStatus ||
    (campaignIsOpen && loggedIn && isCartDataLoading)
  )
    return <LoadingLogo />;

  // redirect redeemed friends to /subscribe-today if socialEcomm FF is OFF
  if (version === VERSION.edit) {
    if (isRedeemedFriend && !socialEcommFF) return redirectToSubscribe();
  }

  if (version === VERSION.shop) {
    const isExpired = subscriptionState === SUBSCRIPTION_STATUS.expired;
    if (!campaignIsOpen || (isExpired && !freeTierEvergreenShopAccessFF)) {
      return redirectToReactivate();
    }
  }

  if (campaignIsOpen) {
    return (
      <Sale
        planCode={planCode}
        displaySocialBanner={displaySocialBanner}
        socialEcommFF={socialEcommFF}
        isSocialEcomm={isSocialEcomm}
      />
    );
  }

  if (version === VERSION.edit) {
    if (isSocialEcomm) {
      if (isRedeemedFriend && !userSocialAccess) return redirectToSubscribe();
      return <ClosedSocialPage planCode={planCode} />;
    }
    if (isRedeemedFriend && campaignStatus === CAMPAIGN_STATUS.ended) {
      return redirectToSubscribe();
    }
    return <ClosedEdit isAccountPastDue={isAccountPastDue} />;
  }

  return <ClosedAddOns campaign={campaign} user={user} />;
};

Scene.propTypes = {
  user: PropTypes.shape({
    fetchingUserStatus: PropTypes.bool,
    planCode: PropTypes.string,
    isAccountPastDue: PropTypes.bool,
    subscriptionState: PropTypes.string,
    loggedIn: PropTypes.bool,
  }).isRequired,
  campaign: PropTypes.shape({
    campaignError: PropTypes.bool,
    fetchingCampaign: PropTypes.bool,
    campaignStatus: PropTypes.string,
    userSocialAccess: PropTypes.bool,
  }).isRequired,
  campaignIsOpen: PropTypes.bool.isRequired,
  isSocialEcomm: PropTypes.bool.isRequired,
  version: PropTypes.string.isRequired,
  fetchingCart: PropTypes.bool.isRequired,
  fetchingPreviousCart: PropTypes.bool.isRequired,
  displaySocialBanner: PropTypes.bool,
};

Scene.defaultProps = {
  displaySocialBanner: false,
};

function mapStateToProps(state) {
  return {
    user: state.userState,
    fetchingCart: state.cartState.fetchingCart,
    fetchingPreviousCart: state.cartState.fetchingPreviousCart,
    campaign: state.campaignState,
  };
}

export default compose(connect(mapStateToProps, {}), withDocumentTitle)(Scene);
